import type { PlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import { TemplateType, getTemplateTypeEntry } from 'modules/manager/domain/template-type';
import React from 'react';
import BracketScheme from './bracket-scheme';
import MatchScheme from './match-scheme';
import MultiMatchScheme from './multi-match-scheme';
import PlayerCardScheme from './player-card-scheme';
import PlayerStatsScheme from './player-stats-scheme';
import { SchemeProvider } from './scheme-parts';
import TableScheme from './table-scheme';

const SCHEMES: Record<TemplateType, React.FC> = {
  [TemplateType.SCHEDULE]: MatchScheme,
  [TemplateType.RESULTS]: MatchScheme,
  [TemplateType.MULTIPLE_SCHEDULE]: MultiMatchScheme,
  [TemplateType.MULTIPLE_RESULTS]: MultiMatchScheme,
  [TemplateType.TOURNAMENT_TABLE]: TableScheme,
  [TemplateType.TOP_PLAYERS_BY_INDICATORS]: PlayerStatsScheme,
  [TemplateType.PLAYOFF_BRACKET]: BracketScheme,
  [TemplateType.BIRTHDAY]: PlayerCardScheme,
  [TemplateType.SYMBOLIC_TEAM_ROUND]: PlayerCardScheme,
  [TemplateType.SYMBOLIC_TEAM_CHAMPIONSHIP]: PlayerCardScheme,
  [TemplateType.VIDEO_INTRO]: MatchScheme,
  [TemplateType.VIDEO_GOAL_PLATE]: PlayerCardScheme,
  [TemplateType.VIDEO_DANGER_PLATE]: MatchScheme,
};

/**
 * Allowed keys a scheme deliberately leaves out — the picker says «На схеме не
 * показан» for them. Empty today: every allowed key of every type has its place.
 * A new key goes either onto its scheme or here; the completeness test fails otherwise.
 */
export const KEYS_NOT_ON_SCHEME: ReadonlySet<PlaceholderKey> = new Set<PlaceholderKey>();

export function isKeyOnScheme(templateType: TemplateType, key: PlaceholderKey): boolean {
  return getTemplateTypeEntry(templateType).allowedPlaceholderKeys.has(key) && !KEYS_NOT_ON_SCHEME.has(key);
}

interface TemplateSchemeProps {
  templateType: TemplateType;
  highlightKey?: PlaceholderKey;
}

/** A sketch of a finished image of the type, with the place of `highlightKey` lit up. */
const TemplateScheme: React.FC<TemplateSchemeProps> = ({ templateType, highlightKey }) => {
  const Scheme = SCHEMES[templateType];
  const allowed = getTemplateTypeEntry(templateType).allowedPlaceholderKeys;
  return (
    <SchemeProvider value={{ allowed, highlightKey }}>
      <Scheme />
    </SchemeProvider>
  );
};

export default React.memo(TemplateScheme);
